import {Query, QueryNavigation} from "@/backend/queries";
import {type Models} from "@hantera/portal-app";
import {QueryTableSelections} from "./table-models";
import {FilterValue, Scalar} from "./filter-models";
import log from "@/log";

export type GraphQueryRequire = 'any' | 'some' | 'none' | boolean

export interface GraphQueryNavigation {
  edge: string,
  require: GraphQueryRequire,
  count?: boolean | number
  orderBy?: string
  filter?: string
  phrase?: string
  after?: string
  before?: string
  limit?: number
  node: {
    fields: string[]
    navigate?: GraphQueryNavigation[]
  }
}

export default function buildQuery(
    query: Query,
    selections: QueryTableSelections,
    graphMeta: Record<string, Models.GraphMeta.GraphNodeMeta>,
    nav: QueryNavigation,
    edges: Record<string, Models.GraphMeta.GraphEdgeMeta>,
    count: boolean | number = 10_000,
    isExport: boolean = false,
    parentPath: string = ''
  ): GraphQueryNavigation | undefined {

    if (nav.edge === undefined) {
      return;
    }

    const currentPath = (parentPath ? parentPath + '.' : '') + nav.edge;
    const currentEdge = edges[nav.edge];
    if (currentEdge === undefined) {
      // This can happen if a query was made against an actor extension that has since been removed.
      log.error(`Attempted to query edge '${nav.edge}' which was not found`);
      return;
    }
    const currentNode = graphMeta[currentEdge.node];

    const columns = query.columns.filter(r => {
      const [fieldName, _] = r.fieldName.split('->')

      // Get columns for the current path
      return r.queryPath.join('.') === currentPath &&
        // Ignore columns that doesn't exist, this can happen if a field has been removed from the graph
        currentNode.fields[fieldName]
    });

    const predicates: string[] = [];
    if (typeof nav.filter === 'string' && nav.filter.trim().length > 0) {
      predicates.push(nav.filter);
    }

    if (selections.filters[currentPath]) {

      for (const field in selections.filters[currentPath]) {

        // Dynamic field columns contains an arrow separator so we try to get the actual dynamic field.
        // Otherwise assume it's a complex field
        let fieldMeta = currentNode.fields[field.split('->')[0]] ?? currentNode.fields[field];
        if (fieldMeta === undefined) {
          continue;
        }

        const filter = selections.filters[currentPath][field];
        if (filter.and.length === 0 && filter.or.length === 0 && filter.not.length === 0) {
          continue;
        }

        if (fieldMeta.valueType.type === 'array') {

          let values = getArrayMask(filter.and);
          if (values.length > 0) {
            predicates.push(`${field} allof [${values.join(',')}]`);
          }

          values = getArrayMask(filter.or);
          if (values.length > 0) {
            predicates.push(`${field} anyof [${values.join(',')}]`);
          }

          values = getArrayMask(filter.not);
          if (values.length > 0) {
            predicates.push(`not(${field} anyof [${values.join(',')}])`);
          }

          function getArrayMask(filterValues: FilterValue[]) {
            
            const isScalarString = fieldMeta.valueType.type === 'array' && fieldMeta.valueType.itemType.type === 'scalar' && fieldMeta.valueType.itemType.scalarType === 'string'
            const isEnum = fieldMeta.valueType.type === 'array' && fieldMeta.valueType.itemType.type === 'enum'
            
            if (!isScalarString && !isEnum) {
              throw new Error('Unsupported type: ' + JSON.stringify(fieldMeta, null, 2));
            }
            
            const result: string[] = [];
            for (const fv of filterValues) {
              result.push(`'${escapeFilter((fv as Scalar).value)}'`);
            }
            return result
          }

        } else {

          const orParts: string[] = [];
          const anyOfParts: string[] = [];

          for (const and of filter.and) {
            const res = formatPredicate(and, field);
            if (res !== undefined) {
              predicates.push(res);
            }
          }
          for (const or of filter.or) {
            if (or.type === 'scalar') {
              let type = fieldMeta.valueType;

              if (type.type === 'scalar' && type.scalarType === 'boolean') {
                anyOfParts.push(or.value ? 'true' : 'false');
              }

              else if (type.type === 'scalar' && type.scalarType === 'uuid') {
                anyOfParts.push(or.value);
              }

              else if (type.type === 'enum') {
                anyOfParts.push(`'${or.value}'`);
              }
              else if (type.type === 'scalar' || type.type === 'dynamicFields') {
                if (!or.value) {
                  anyOfParts.push(`''`);
                  anyOfParts.push(`nothing`);
                }

                anyOfParts.push(`'${escapeFilter(or.value)}'`);
              }
              
            } else {
              const res = formatPredicate(or, field);
              if (res !== undefined) {
                orParts.push(res);
              }
            }
          }
          for (const not of filter.not) {
            const res = formatPredicate(not, field);
            if (res !== undefined) {
              predicates.push(`not (${res})`);
            }
          }
          if (orParts.length > 0) {
            predicates.push(`(${orParts.join(' or ')})`);
          }
          if (anyOfParts.length > 0) {
            predicates.push(`${field} anyof [${anyOfParts.join(',')}]`);
          }
        }

        function formatPredicate(value: FilterValue, field: string) {
          
          if (fieldMeta.filter.type === 'instantRange') {
            if (value.type !== 'dateRange') {
              return;
            }

            if (value.rangeType === 'absolute') {
              return `${field} >= ${value.from} and ${field} < ${value.to}`;
            }

            if (value.rangeType === 'relativeToday') {
              const from = new Date();
              from.setHours(0, 0, 0, 0);
              const to = new Date();
              to.setDate(to.getDate() + 1);
              to.setHours(0, 0, 0, 0);
              return `${field} >= ${from.toISOString()} and ${field} < ${to.toISOString()}`;
            }

            if (value.rangeType === 'relativeYesterday') {
              const from = new Date();
              from.setDate(from.getDate() - 1);
              from.setHours(0, 0, 0, 0);
              const to = new Date();
              to.setHours(0, 0, 0, 0);
              return `${field} >= ${from.toISOString()} and ${field} < ${to.toISOString()}`;
            }

            if (value.rangeType === 'relativeThisMonth') {
              const from = new Date();
              from.setDate(1);
              from.setHours(0, 0, 0, 0);
              const to = new Date();
              to.setMonth(to.getMonth() + 1);
              to.setDate(1);
              to.setHours(0, 0, 0, 0);
              return `${field} >= ${from.toISOString()} and ${field} < ${to.toISOString()}`;
            }

            if (value.rangeType === 'relativeLastMonth') {
              const from = new Date();
              from.setMonth(from.getMonth() - 1);
              from.setDate(1);
              from.setHours(0, 0, 0, 0);
              const to = new Date();
              to.setDate(1);
              to.setHours(0, 0, 0, 0);
              return `${field} >= ${from.toISOString()} and ${field} < ${to.toISOString()}`;
            }

            if (value.rangeType === 'relativeDays') {
              const from = new Date();
              from.setDate(from.getDate() - value.days);
              from.setHours(0, 0, 0, 0);
              const to = new Date();
              to.setHours(0, 0, 0, 0);
              return `${field} >= ${from.toISOString()} and ${field} < ${to.toISOString()}`;
            }

            console.warn('Unsupported date range', value);
            return;
          }
          if (fieldMeta.filter.type === 'numericRange') {
            if (value.type !== 'numericRange') {
              return;
            }
            if (value.from === value.to) {
              return `${field} == ${value.from}`;
            }
            if (value.to === Number.MAX_VALUE) {
              return `${field} >= ${value.from}`;
            }
            if (value.from === Number.MIN_VALUE) {
              return `${field} <= ${value.to}`;
            }
            return `${field} >= ${value.from} and ${field} <= ${value.to}`;
          }

          let type = fieldMeta.valueType;
          if (fieldMeta.valueType.type === 'array') {
            type = fieldMeta.valueType.itemType;
          }

          if (type.type === 'scalar' && type.scalarType === 'boolean') {
            if (value.type !== 'scalar') {
              return;
            }
            return `${field} == ${value.value ? 'true' : 'false'}`;
          }

          if (type.type === 'scalar' && type.scalarType === 'uuid') {
            console.log('AA', type, value)
            if (value.type !== 'scalar') {
              return;
            }
            if (!value.value) {
              return `(${field} == nothing)`;
            }
            
            return `${field} == ${value.value}`;
          }

          if (type.type === 'enum') {
            if (value.type !== 'scalar') {
              return;
            }
            return `${field} == '${value.value}'`;
          }
          if (type.type === 'scalar' || type.type === 'dynamicFields') {
            if (value.type !== 'scalar') {
              return;
            }
            if (!value.value) {
              return `(${field} == '' or ${field} == nothing)`;
            }
            
            return `${field} == '${escapeFilter(value.value)}'`;
          }
        }
      }

    }

    const fields = columns.map(c => c.fieldName);
    const primaryKey = currentNode.idField;

    if (!isExport && fields.indexOf(primaryKey) === -1) {
      fields.splice(0, 0, primaryKey);
    }

    let orderBy: string | undefined = undefined;

    if (!parentPath) {
      if (selections.order !== undefined) {
        orderBy = `${selections.order.field} ${selections.order!.direction}`;
      }

      if (!orderBy && currentNode.fields['createdAt'] !== undefined) {
        orderBy = 'createdAt desc';
      } else if (!orderBy && fields.length > 0) {
        orderBy = `${fields[0]} asc`;
      }
    }
    
    let forceRequire = false;
    if (parentPath) {
      // The graph engine requires single edges that is used in "orderBy" to be required
      
      // currentPath contains the root navigation, while the order field is relative to the root edge
      // So we need to drop the root edge from the current path to be able to match with the order field.
      let match = currentPath.substring(currentPath.indexOf('.') + 1);
      forceRequire = selections.order?.field.startsWith('`' + match + '`') ?? false; 
    }

    const result = {
      edge: nav.edge,
      require: (forceRequire || predicates.length > 0) ? 'some' : 'any',
      orderBy,
      count,
      node: {
        fields: fields,
        navigate: nav.navigation?.map(n => buildQuery(query, selections, graphMeta, n, currentNode.edges, false, isExport, currentPath)).filter(r => r !== undefined),
      },
      filter: predicates.length > 0 ? `(${predicates.join(') and (')})` : undefined,
    } as GraphQueryNavigation;

    if (currentEdge.cardinality === 'many') {
      result.limit = parentPath === '' ? 100 : 1000;
    }

    return result;
  };

function escapeFilter(value: string) {
  return value?.replaceAll("'", "''")
}